import axios from 'axios'
import { getCookieByName, clearAllCookies } from '../../utils'

// URL resolvida por ambiente: prioriza variável explícita, senão usa build stage
const OPEN_FINANCE_URL =
    process.env.REACT_APP_OPEN_FINANCE_URL ||
    (process.env.REACT_APP_BUILD_STAGE === 'homolog'
        ? 'http://vps1.easygestor.com:8001'
        : 'https://cron-jobs-open-finance-api.pc9f6u.easypanel.host')

export const openFinanceApi = axios.create({
    baseURL: OPEN_FINANCE_URL,
    headers: {
        "Content-Type": "application/json",
    }
})

openFinanceApi.interceptors.request.use(
    function (config) {
        var access = getCookieByName('access')
        if (access) {
            config.headers['Authorization'] = 'Bearer ' + access
        }

        var branchOwner = getCookieByName('branch_owner_id')
        if (branchOwner) {
            config.headers['X-Company-ID'] = branchOwner
        }

        return config
    },
    function (error) {
        return Promise.reject(error)
    }
)

if (process.env.REACT_APP_BUILD_STAGE !== 'production') {
    console.debug("OpenFinance API Client Initialized:", { baseURL: OPEN_FINANCE_URL });
}

async function _refreshToken() {
    return new Promise((resolve, reject) => {
        const refresh = getCookieByName('refresh')
        const API_URL = require('../../utils/config').getConfig().api.baseUrl;
        axios
            .post(`${API_URL}/api/token/refresh/`, { refresh })
            .then(response => {
                const date = new Date();
                date.setTime(date.getTime() + (24 * 60 * 60 * 1000));
                document.cookie = `access=${response.data.access}; path=/; expires=${date.toUTCString()}`;
                resolve(response.data.access);
            })
            .catch(error => {
                if (error.response) {
                    clearAllCookies();
                    window.localStorage.clear();
                    window.location.href = '/';
                }
                reject(error);
            });
    });
}

openFinanceApi.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const access = await _refreshToken();
                originalRequest.headers['Authorization'] = `Bearer ${access}`;
                return openFinanceApi(originalRequest);
            } catch (err) {
                return Promise.reject(err);
            }
        }
        return Promise.reject(error.response ? error.response.data : error);
    }
);

export function getOpenFinanceCompanies() {
    return openFinanceApi.get(`/companies/`)
}

export function syncBankStatement(data, config) {
    const branchOwner = (config && config.params && config.params.branch_owner) || "not-set";
    return openFinanceApi.post(`/companies/${branchOwner}/bank/sync`, data)
}

export function getBankStatementPreview(data, config) {
    const branchOwner = (config && config.params && config.params.branch_owner) || "not-set";
    return openFinanceApi.post(`/companies/${branchOwner}/bank/preview`, data)
}

export function setupIntegration(data, config) {
    const branchOwner = (config && config.params && config.params.branch_owner) || "not-set";
    // Nota: 'data' agora deve ser um objeto literal/JSON e não um FormData
    return openFinanceApi.post(`/companies/${branchOwner}/credentials/`, data)
}

export function deleteIntegrationSetup(provider, config) {
    const branchOwner = (config && config.params && config.params.branch_owner) || "not-set";
    return openFinanceApi.delete(`/companies/${branchOwner}/credentials/${provider}`)
}

export const getOpenFinanceCategories = (companyId) => openFinanceApi.get(`/companies/${companyId}/categories/`).then(res => res.data);
export const createOpenFinanceCategory = (companyId, data) => openFinanceApi.post(`/companies/${companyId}/categories/`, data).then(res => res.data);
export const deleteOpenFinanceCategory = (companyId, categoryId) => openFinanceApi.delete(`/companies/${companyId}/categories/${categoryId}`).then(res => res.data);
export const updateTransactionCategory = (companyId, transactionId, data) => openFinanceApi.patch(`/companies/${companyId}/bank/transactions/${transactionId}`, data).then(res => res.data);
export const deleteBankTransaction = (companyId, transactionId) => openFinanceApi.delete(`/companies/${companyId}/bank/transactions/${transactionId}/`).then(res => res.data);

// ── Caixas & Contas Bancárias ─────────────────────────────────────────────────
// O backend /bank-accounts/* usa o JWT do Django (cookie 'access'), que já contém
// branch_owner → mapeado para company_id pelo dependencies.py.

/** Lê o JWT do cookie 'access' (mesmo token usado pela app toda). */
function _getCookieAccess() {
    const cookies = document.cookie.split(';')
    const c = cookies.find(ck => ck.trim().startsWith('access='))
    return c ? c.trim().split('=')[1] : ''
}

/** Cabeçalho de auth com o JWT do Django. */
function _authHeader() {
    return { Authorization: `Bearer ${_getCookieAccess()}` }
}

/**
 * Retorna sumário consolidado: saldo total, stats de 30d por conta, counters OF/físico.
 */
export const getBankAccountsSummary = () =>
    openFinanceApi.get(`/bank-accounts/summary`, { headers: _authHeader() }).then(res => res.data)

/** Lista todas as contas/caixas da empresa. */
export const getBankAccounts = () =>
    openFinanceApi.get(`/bank-accounts/`, { headers: _authHeader() }).then(res => res.data)

/** Cria uma nova conta ou caixa. */
export const createBankAccount = (data) =>
    openFinanceApi.post(`/bank-accounts/`, data, { headers: _authHeader() }).then(res => res.data)

/** Atualiza dados de uma conta existente. */
export const updateBankAccount = (accountId, data) =>
    openFinanceApi.put(`/bank-accounts/${accountId}`, data, { headers: _authHeader() }).then(res => res.data)

/** Remove uma conta/caixa. */
export const deleteBankAccount = (accountId) =>
    openFinanceApi.delete(`/bank-accounts/${accountId}`, { headers: _authHeader() })



/** Transfere dinheiro entre dois caixas. */
export const transferBetweenAccounts = (data) =>
    openFinanceApi.post(`/bank-accounts/transfer`, data, { headers: _authHeader() }).then(res => res.data)




// WEBPACK FOOTER //
// ./src/api/integrations/index.js